import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Building2, User, Mail, Phone, MapPin, IndianRupee, Users, 
  Image, FileText, CheckCircle, ArrowRight, Menu, X, Hotel,
  Wifi, Monitor, Car, Utensils, Dumbbell, Waves, Sparkles,
  Coffee, Briefcase, Plane, ConciergeBell, Upload, Star
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/utils/api';

export default function VendorRegisterPage() {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    // Account Details
    contactName: '',
    email: '',
    phone: '',
    designation: '',
    
    // Hotel Details
    hotelName: '',
    brandName: '',
    propertyType: 'hotel',
    starCategory: '5-star',
    address: '',
    city: '',
    state: '',
    pinCode: '',
    
    // Capacity & Pricing
    totalRooms: '',
    maxConferenceCapacity: '',
    startingPricePerDay: '',
    
    // Venue Details
    numberOfBanquetHalls: '',
    largestHallCapacity: '',
    outdoorVenueAvailable: false,
    boardroomCapacity: '',
    
    // Amenities
    amenities: [],
    
    // Documents
    gstin: '',
    pan: '',
    
    // Description
    description: '',
    highlights: '',
    notableClients: ''
  });

  const amenitiesList = [
    { id: 'wifi', label: 'High-Speed WiFi', icon: Wifi },
    { id: 'av_equipment', label: 'Projector & AV Equipment', icon: Monitor },
    { id: 'video_conferencing', label: 'Video Conferencing', icon: Monitor },
    { id: 'business_center', label: 'Business Center', icon: Briefcase },
    { id: 'swimming_pool', label: 'Swimming Pool', icon: Waves },
    { id: 'spa', label: 'Spa & Wellness', icon: Sparkles },
    { id: 'gym', label: 'Gym/Fitness Center', icon: Dumbbell },
    { id: 'restaurant', label: 'Multi-cuisine Restaurant', icon: Utensils },
    { id: 'bar', label: 'Bar/Lounge', icon: Coffee },
    { id: 'valet_parking', label: 'Valet Parking', icon: Car },
    { id: 'airport_transfer', label: 'Airport Transfer', icon: Plane },
    { id: 'concierge', label: 'Concierge Service', icon: ConciergeBell }
  ];

  React.useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAmenityToggle = (amenityId) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenityId)
        ? prev.amenities.filter(id => id !== amenityId)
        : [...prev.amenities, amenityId]
    }));
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!formData.contactName || !formData.email || !formData.phone || !formData.designation) {
          toast.error('Please fill all required fields');
          return false;
        }
        if (!/\S+@\S+\.\S+/.test(formData.email)) {
          toast.error('Please enter a valid email address');
          return false;
        }
        return true;
      case 2:
        if (!formData.hotelName || !formData.address || !formData.city || !formData.state) {
          toast.error('Please fill all required hotel details');
          return false;
        }
        return true;
      case 3:
        if (!formData.totalRooms || !formData.maxConferenceCapacity || !formData.startingPricePerDay) {
          toast.error('Please fill capacity and pricing details');
          return false;
        }
        return true;
      case 4:
        if (formData.amenities.length < 3) {
          toast.error('Please select at least 3 amenities');
          return false;
        }
        return true;
      case 5:
        if (!formData.description) {
          toast.error('Please provide a hotel description');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 5));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep(5)) return;
    
    setIsSubmitting(true);
    
    try {
      // Create vendor registration request
      const registrationData = {
        name: formData.contactName,
        email: formData.email,
        password: 'TempPassword123!', // Temporary password - will be reset
        phone: formData.phone,
        company: formData.hotelName,
        role: 'vendor',
        vendor_details: {
          designation: formData.designation,
          hotel_name: formData.hotelName,
          brand_name: formData.brandName,
          property_type: formData.propertyType,
          star_category: formData.starCategory,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          pin_code: formData.pinCode,
          total_rooms: parseInt(formData.totalRooms),
          max_conference_capacity: parseInt(formData.maxConferenceCapacity),
          starting_price_per_day: parseFloat(formData.startingPricePerDay),
          number_of_banquet_halls: parseInt(formData.numberOfBanquetHalls) || 0,
          largest_hall_capacity: parseInt(formData.largestHallCapacity) || 0,
          outdoor_venue_available: formData.outdoorVenueAvailable,
          boardroom_capacity: parseInt(formData.boardroomCapacity) || 0,
          amenities: formData.amenities,
          gstin: formData.gstin,
          pan: formData.pan,
          description: formData.description,
          highlights: formData.highlights,
          notable_clients: formData.notableClients
        }
      };

      await api.register(registrationData);
      
      toast.success('Registration submitted successfully! Our team will review and contact you within 24-48 hours.');
      
      // Redirect to success page or home
      setTimeout(() => {
        navigate('/?registration=success');
      }, 2000);
      
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const steps = [
    { number: 1, title: 'Account Details', icon: User },
    { number: 2, title: 'Hotel Details', icon: Hotel },
    { number: 3, title: 'Capacity & Pricing', icon: IndianRupee },
    { number: 4, title: 'Amenities', icon: Sparkles },
    { number: 5, title: 'Description', icon: FileText }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-lg shadow-md' 
          : 'bg-white shadow-sm'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_07322645-a840-4848-8247-f88636798b30/artifacts/kyqhtx8f_MCH%20Logo-2.png" 
                alt="My Corporate Hub" 
                className="h-12 w-auto"
              />
              <span className="font-heading text-xl text-navy font-bold tracking-tight hidden md:block">My Corporate Hub</span>
            </Link>
            
            <div className="flex items-center gap-4">
              <Link to="/?login=true" className="hidden md:block">
                <Button variant="ghost" className="text-navy font-medium">Sign In</Button>
              </Link>
              <Link to="/">
                <Button className="bg-gold hover:bg-gold-light text-navy-900 font-bold shadow-lg px-6">
                  Back to Home
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-28 pb-12 bg-gradient-to-r from-navy-900 to-navy-800">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <span className="inline-flex items-center bg-gold/10 text-gold border border-gold/30 px-6 py-2 rounded-full text-sm font-bold uppercase tracking-widest mb-6">
            <Building2 className="w-4 h-4 mr-2" /> Partner With Us
          </span>
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-white mb-4">
            Register Your Hotel
          </h1>
          <p className="text-slate-300 text-lg max-w-2xl mx-auto">
            Join India's premier corporate events platform. Get access to 500+ corporate clients and grow your business.
          </p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-gold/10 rounded-full flex items-center justify-center mb-2">
                <Users className="w-6 h-6 text-gold" />
              </div>
              <p className="font-semibold text-navy">500+ Corporates</p>
              <p className="text-sm text-slate-500">Active clients</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mb-2">
                <IndianRupee className="w-6 h-6 text-emerald-600" />
              </div>
              <p className="font-semibold text-navy">Zero Listing Fee</p>
              <p className="text-sm text-slate-500">No upfront cost</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-2">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <p className="font-semibold text-navy">Real-time RFPs</p>
              <p className="text-sm text-slate-500">Instant notifications</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-2">
                <Star className="w-6 h-6 text-purple-600" />
              </div>
              <p className="font-semibold text-navy">Premium Visibility</p>
              <p className="text-sm text-slate-500">Featured listings</p>
            </div>
          </div>
        </div>
      </section>

      {/* Registration Form */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4">
          {/* Progress Steps */}
          <div className="mb-10">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => (
                <React.Fragment key={step.number}>
                  <div className="flex flex-col items-center">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg transition-all ${
                      currentStep >= step.number 
                        ? 'bg-navy text-white' 
                        : 'bg-slate-200 text-slate-500'
                    }`}>
                      {currentStep > step.number ? (
                        <CheckCircle className="w-6 h-6" />
                      ) : (
                        step.number
                      )}
                    </div>
                    <p className={`text-xs mt-2 font-medium hidden md:block ${
                      currentStep >= step.number ? 'text-navy' : 'text-slate-400'
                    }`}>
                      {step.title}
                    </p>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`flex-1 h-1 mx-2 rounded ${
                      currentStep > step.number ? 'bg-navy' : 'bg-slate-200'
                    }`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          <Card className="shadow-xl border-0">
            <CardHeader className="bg-slate-50 border-b">
              <CardTitle className="text-2xl text-navy flex items-center gap-3">
                {React.createElement(steps[currentStep - 1].icon, { className: "w-6 h-6 text-gold" })}
                {steps[currentStep - 1].title}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-8">
              <form onSubmit={handleSubmit}>
                {/* Step 1: Account Details */}
                {currentStep === 1 && (
                  <div className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Contact Person Name <span className="text-red-500">*</span>
                        </label>
                        <Input
                          name="contactName"
                          value={formData.contactName}
                          onChange={handleInputChange}
                          placeholder="Enter full name"
                          className="h-12"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Designation <span className="text-red-500">*</span>
                        </label>
                        <Input
                          name="designation"
                          value={formData.designation}
                          onChange={handleInputChange}
                          placeholder="e.g., Sales Manager, GM"
                          className="h-12"
                          required
                        />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Email Address <span className="text-red-500">*</span>
                        </label>
                        <Input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="sales@yourhotel.com"
                          className="h-12"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Phone Number <span className="text-red-500">*</span>
                        </label>
                        <Input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="+91 9876543210"
                          className="h-12"
                          required
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 2: Hotel Details */}
                {currentStep === 2 && (
                  <div className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Hotel Name <span className="text-red-500">*</span>
                        </label>
                        <Input
                          name="hotelName"
                          value={formData.hotelName}
                          onChange={handleInputChange}
                          placeholder="e.g., Taj Mahal Palace Mumbai"
                          className="h-12"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Brand/Chain Name
                        </label>
                        <Input
                          name="brandName"
                          value={formData.brandName}
                          onChange={handleInputChange}
                          placeholder="e.g., TAJ, OBEROI, ITC"
                          className="h-12"
                        />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Property Type <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="propertyType"
                          value={formData.propertyType}
                          onChange={handleInputChange}
                          className="w-full h-12 px-4 border border-slate-300 rounded-md focus:ring-2 focus:ring-gold focus:border-transparent"
                        >
                          <option value="hotel">Hotel</option>
                          <option value="resort">Resort</option>
                          <option value="palace">Heritage Palace</option>
                          <option value="convention_center">Convention Center</option>
                          <option value="boutique">Boutique Hotel</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Star Category <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="starCategory"
                          value={formData.starCategory}
                          onChange={handleInputChange}
                          className="w-full h-12 px-4 border border-slate-300 rounded-md focus:ring-2 focus:ring-gold focus:border-transparent"
                        >
                          <option value="5-star-deluxe">5-Star Deluxe</option>
                          <option value="5-star">5-Star</option>
                          <option value="4-star">4-Star</option>
                          <option value="heritage">Heritage</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Complete Address <span className="text-red-500">*</span>
                      </label>
                      <Input
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        placeholder="Street address, landmark"
                        className="h-12"
                        required
                      />
                    </div>
                    <div className="grid md:grid-cols-3 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          City <span className="text-red-500">*</span>
                        </label>
                        <Input
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          placeholder="Mumbai"
                          className="h-12"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          State <span className="text-red-500">*</span>
                        </label>
                        <Input
                          name="state"
                          value={formData.state}
                          onChange={handleInputChange}
                          placeholder="Maharashtra"
                          className="h-12"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          PIN Code
                        </label>
                        <Input
                          name="pinCode"
                          value={formData.pinCode}
                          onChange={handleInputChange}
                          placeholder="400001"
                          className="h-12"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 3: Capacity & Pricing */}
                {currentStep === 3 && (
                  <div className="space-y-6">
                    <div className="grid md:grid-cols-3 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Total Rooms <span className="text-red-500">*</span>
                        </label>
                        <Input
                          type="number"
                          name="totalRooms"
                          value={formData.totalRooms}
                          onChange={handleInputChange}
                          placeholder="e.g., 250"
                          className="h-12"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Max Conference Capacity <span className="text-red-500">*</span>
                        </label>
                        <Input
                          type="number"
                          name="maxConferenceCapacity"
                          value={formData.maxConferenceCapacity}
                          onChange={handleInputChange}
                          placeholder="e.g., 500"
                          className="h-12"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Starting Price/Day (₹) <span className="text-red-500">*</span>
                        </label>
                        <Input
                          type="number"
                          name="startingPricePerDay"
                          value={formData.startingPricePerDay}
                          onChange={handleInputChange}
                          placeholder="e.g., 150000"
                          className="h-12"
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="border-t pt-6">
                      <h3 className="text-lg font-semibold text-navy mb-4">Venue Details</h3>
                      <div className="grid md:grid-cols-3 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Number of Banquet Halls
                          </label>
                          <Input
                            type="number"
                            name="numberOfBanquetHalls"
                            value={formData.numberOfBanquetHalls}
                            onChange={handleInputChange}
                            placeholder="e.g., 5"
                            className="h-12"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Largest Hall Capacity
                          </label>
                          <Input
                            type="number"
                            name="largestHallCapacity"
                            value={formData.largestHallCapacity}
                            onChange={handleInputChange}
                            placeholder="e.g., 1000"
                            className="h-12"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Boardroom Capacity
                          </label>
                          <Input
                            type="number"
                            name="boardroomCapacity"
                            value={formData.boardroomCapacity}
                            onChange={handleInputChange}
                            placeholder="e.g., 20"
                            className="h-12"
                          />
                        </div>
                      </div>
                      <div className="mt-4">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            name="outdoorVenueAvailable"
                            checked={formData.outdoorVenueAvailable}
                            onChange={handleInputChange}
                            className="w-5 h-5 rounded border-slate-300 text-gold focus:ring-gold"
                          />
                          <span className="text-sm font-medium text-slate-700">Outdoor Venue Available</span>
                        </label>
                      </div>
                    </div>

                    <div className="border-t pt-6">
                      <h3 className="text-lg font-semibold text-navy mb-4">Business Documents (Optional)</h3>
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            GSTIN Number
                          </label>
                          <Input
                            name="gstin"
                            value={formData.gstin}
                            onChange={handleInputChange}
                            placeholder="e.g., 27AABCU9603R1ZM"
                            className="h-12"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            PAN Number
                          </label>
                          <Input
                            name="pan"
                            value={formData.pan}
                            onChange={handleInputChange}
                            placeholder="e.g., ABCDE1234F"
                            className="h-12"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 4: Amenities */}
                {currentStep === 4 && (
                  <div className="space-y-6">
                    <p className="text-slate-600 mb-4">Select all amenities available at your property (minimum 3 required)</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {amenitiesList.map((amenity) => (
                        <label
                          key={amenity.id}
                          className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                            formData.amenities.includes(amenity.id)
                              ? 'border-gold bg-gold/5'
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={formData.amenities.includes(amenity.id)}
                            onChange={() => handleAmenityToggle(amenity.id)}
                            className="w-5 h-5 rounded border-slate-300 text-gold focus:ring-gold"
                          />
                          <amenity.icon className={`w-5 h-5 ${
                            formData.amenities.includes(amenity.id) ? 'text-gold' : 'text-slate-400'
                          }`} />
                          <span className="text-sm font-medium text-slate-700">{amenity.label}</span>
                        </label>
                      ))}
                    </div>
                    <p className="text-sm text-slate-500">
                      Selected: {formData.amenities.length} amenities
                    </p>
                  </div>
                )}

                {/* Step 5: Description */}
                {currentStep === 5 && (
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Hotel Description <span className="text-red-500">*</span>
                      </label>
                      <Textarea
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        placeholder="Describe your hotel, its unique features, and why it's perfect for corporate events (100-300 words)"
                        className="min-h-[120px]"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Unique Selling Points / Highlights
                      </label>
                      <Textarea
                        name="highlights"
                        value={formData.highlights}
                        onChange={handleInputChange}
                        placeholder="What makes your hotel special for corporate events? e.g., Award-winning restaurants, State-of-the-art AV, Heritage property"
                        className="min-h-[100px]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Notable Corporate Clients
                      </label>
                      <Textarea
                        name="notableClients"
                        value={formData.notableClients}
                        onChange={handleInputChange}
                        placeholder="List any notable corporate clients you've hosted (optional)"
                        className="min-h-[80px]"
                      />
                    </div>

                    {/* Summary */}
                    <div className="bg-slate-50 rounded-xl p-6 mt-6">
                      <h3 className="text-lg font-semibold text-navy mb-4">Registration Summary</h3>
                      <div className="grid md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-slate-500">Hotel Name</p>
                          <p className="font-medium text-navy">{formData.hotelName || '-'}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Contact Person</p>
                          <p className="font-medium text-navy">{formData.contactName || '-'}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Email</p>
                          <p className="font-medium text-navy">{formData.email || '-'}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">City</p>
                          <p className="font-medium text-navy">{formData.city || '-'}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Total Rooms</p>
                          <p className="font-medium text-navy">{formData.totalRooms || '-'}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Starting Price/Day</p>
                          <p className="font-medium text-navy">₹{formData.startingPricePerDay ? parseInt(formData.startingPricePerDay).toLocaleString() : '-'}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Navigation Buttons */}
                <div className="flex justify-between mt-10 pt-6 border-t">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={prevStep}
                    disabled={currentStep === 1}
                    className="px-8"
                  >
                    Previous
                  </Button>
                  
                  {currentStep < 5 ? (
                    <Button
                      type="button"
                      onClick={nextStep}
                      className="bg-navy hover:bg-navy-800 text-white px-8"
                    >
                      Next Step <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  ) : (
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="bg-gold hover:bg-gold-light text-navy font-bold px-10"
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit Registration'}
                    </Button>
                  )}
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Contact Info */}
          <div className="mt-8 text-center">
            <p className="text-slate-600">
              Need help? Contact us at{' '}
              <a href="mailto:partners@mycorporatehub.com" className="text-gold font-medium hover:underline">
                partners@mycorporatehub.com
              </a>
              {' '}or call{' '}
              <a href="tel:+919876543210" className="text-gold font-medium hover:underline">
                +91 98765 43210
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-navy text-white py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-slate-400">© 2024 My Corporate Hub. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
